import React from 'react';
import { useQuery } from '@apollo/client';
import { GET_USER_MANAGER_PERMISSION_TEMPLATE_CONFIG } from '../queries';
import {
  normalizeUserManagerPermissionTemplateConfig,
  USER_MANAGER_PERMISSION_TEMPLATE_OPTIONS,
  USER_MANAGER_PERMISSION_TEMPLATE_MANAGED_VALUES
} from '../userManagerPermissionTemplates';
import { PARTICIPANT_PERMISSION_KEYS } from '@/utils/participantSidebarPolicies';

export const PARTICIPANT_PERMISSION_SETTING_KEYS = PARTICIPANT_PERMISSION_KEYS;

const SIMPLE_TOOL_GROUP_VALUES = new Set([
  'tools:reports:access',
  'tools:inrepaccess',
  'tools:progressreportaccess',
  'tools:efax:access',
  'tools:dd2',
  'tools:caseconference',
  'tools:courtcalendar',
  'tools:housing'
]);

const simplePermissionBlocks = [
  {
    title: 'Programs',
    items: USER_MANAGER_PERMISSION_TEMPLATE_OPTIONS.filter((option) => option.category === 'Programs')
  },
  {
    title: 'Participant',
    items: USER_MANAGER_PERMISSION_TEMPLATE_OPTIONS.filter((option) => option.category === 'Participant Sections')
  },
  {
    title: 'Tools',
    items: USER_MANAGER_PERMISSION_TEMPLATE_OPTIONS.filter((option) =>
      option.category === 'Tools' && SIMPLE_TOOL_GROUP_VALUES.has(option.value)
    )
  }
];

interface UserPermission {
  permission: string;
  enabled: boolean;
}

interface PermissionManagerProps {
  selectedPermissions: UserPermission[];
  onChange: (permissions: UserPermission[]) => void;
  disabled?: boolean;
  inlineToolsPanel?: React.ReactNode;
  integratedToolsPanel?: React.ReactNode;
  hidePresetSelector?: boolean;
}

const updatePermissionList = (
  permissions: UserPermission[],
  updates: Record<string, boolean>
) => {
  const selected = new Map<string, string>();

  (Array.isArray(permissions) ? permissions : []).forEach((permissionState) => {
    if (permissionState?.enabled === false) {
      return;
    }

    const permissionName = `${permissionState?.permission || ''}`.trim();
    const permissionNameKey = permissionName.toLowerCase();
    if (!permissionNameKey) {
      return;
    }

    selected.set(permissionNameKey, permissionName);
  });

  Object.entries(updates).forEach(([permissionName, enabled]) => {
    const permissionNameKey = permissionName.toLowerCase();
    if (!enabled) {
      selected.delete(permissionNameKey);
      return;
    }

    selected.set(permissionNameKey, permissionName);
  });

  return Array.from(selected.values()).map((permission) => ({
    permission,
    enabled: true
  }));
};

export const PermissionPresetSelect: React.FC<{
  selectedPermissions: UserPermission[];
  onChange: (permissions: UserPermission[]) => void;
  disabled?: boolean;
  className?: string;
}> = ({
  selectedPermissions,
  onChange,
  disabled = false,
  className = ''
}) => {
  const [generalAccessTemplateSelection, setGeneralAccessTemplateSelection] = React.useState('');
  const { data: templateConfigData } = useQuery(GET_USER_MANAGER_PERMISSION_TEMPLATE_CONFIG, {
    fetchPolicy: 'cache-and-network'
  });
  const generalAccessTemplates = React.useMemo(
    () => normalizeUserManagerPermissionTemplateConfig(
      (templateConfigData as any)?.getUserManagerPermissionTemplateConfig
    ).templates,
    [templateConfigData]
  );
  const managedTemplatePermissionSet = React.useMemo(
    () => new Set(USER_MANAGER_PERMISSION_TEMPLATE_MANAGED_VALUES.map((value) => `${value || ''}`.trim().toLowerCase())),
    []
  );

  const applyGeneralAccessTemplate = (nextValue: string) => {
    setGeneralAccessTemplateSelection(nextValue);
    if (!nextValue || disabled) {
      return;
    }

    const selectedTemplate = generalAccessTemplates.find((template) => template.id === nextValue);
    if (!selectedTemplate) {
      return;
    }

    const selectedTemplatePermissions = new Set(
      selectedTemplate.permissions.map((permissionName) => `${permissionName || ''}`.trim().toLowerCase())
    );
    const preservedSelections = selectedPermissions.filter((permissionState) => {
      if (permissionState?.enabled === false) {
        return false;
      }
      const permissionKey = `${permissionState?.permission || ''}`.trim().toLowerCase();
      return permissionKey && !managedTemplatePermissionSet.has(permissionKey);
    });
    const managedSelections = USER_MANAGER_PERMISSION_TEMPLATE_MANAGED_VALUES.map((permission) => ({
      permission,
      enabled: selectedTemplatePermissions.has(`${permission || ''}`.trim().toLowerCase())
    }));

    onChange([...preservedSelections, ...managedSelections]);
  };

  return (
    <select
      aria-label="Choose a role preset"
      value={generalAccessTemplateSelection}
      onChange={(event) => applyGeneralAccessTemplate(event.target.value)}
      disabled={disabled}
      className={`h-6 min-w-[168px] rounded border border-gray-200 bg-white px-1.5 text-[11px] leading-none text-gray-700 ${
        disabled ? 'opacity-60' : ''
      } ${className}`}
    >
      <option value="">Choose a role preset</option>
      {generalAccessTemplates.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

export const PermissionManager: React.FC<PermissionManagerProps> = ({
  selectedPermissions,
  onChange,
  disabled = false,
  inlineToolsPanel,
  integratedToolsPanel,
  hidePresetSelector = false
}) => {
  const selectedPermissionSet = React.useMemo(
    () =>
      new Set(
        selectedPermissions
          .filter((permissionState) => permissionState.enabled !== false)
          .map((permissionState) => `${permissionState.permission || ''}`.trim().toLowerCase())
          .filter(Boolean)
      ),
    [selectedPermissions]
  );

  const applyAccessUpdates = (updates: Record<string, boolean>) => {
    if (disabled) return;
    onChange(updatePermissionList(selectedPermissions, updates));
  };

  const toggleAccess = (access: string) => {
    const accessEnabled = selectedPermissionSet.has(access);
    applyAccessUpdates({ [access]: !accessEnabled });
  };

  return (
    <div className="space-y-6" data-testid="user-manager-simple-permission-groups">
      {!hidePresetSelector && (
        <div className="flex items-center justify-end">
          <PermissionPresetSelect
            selectedPermissions={selectedPermissions}
            onChange={onChange}
            disabled={disabled}
          />
        </div>
      )}
      {simplePermissionBlocks.map((block) => {
        const isToolsBlock = block.title === 'Tools';
        return (
          <section key={block.title} className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-bold text-gray-900">{block.title}</p>
            </div>
            <div className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2 lg:grid-cols-5">
              {block.items.map((item) => {
                const inputId = `user-manager-simple-${item.value.replace(/[^a-z0-9]+/gi, '-')}`;
                return (
                  <label
                    key={item.value}
                    htmlFor={inputId}
                    className={`flex items-center gap-2 text-sm ${
                      disabled ? 'text-gray-400' : 'text-gray-900'
                    }`}
                  >
                    <input
                      id={inputId}
                      type="checkbox"
                      checked={selectedPermissionSet.has(item.value)}
                      onChange={() => toggleAccess(item.value)}
                      disabled={disabled}
                    />
                    <span>{item.label}</span>
                  </label>
                );
              })}
              {isToolsBlock && inlineToolsPanel}
            </div>
            {isToolsBlock && integratedToolsPanel && (
              <div>{integratedToolsPanel}</div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default PermissionManager;
